import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Coffee, Play, Square } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const ACTS = [
  { k: "desk", l: "Desk Work" },
  { k: "computer", l: "Computer" },
  { k: "reading", l: "Reading" },
  { k: "driving", l: "Driving" },
  { k: "meeting", l: "Meeting" },
  { k: "tv", l: "Watching TV" },
  { k: "meditation", l: "Meditation" },
  { k: "travel", l: "Travel" },
  { k: "other", l: "Other" },
];

export default function Passive() {
  const nav = useNavigate();
  const [activity, setActivity] = useState("desk");
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [quality, setQuality] = useState(8);
  const intRef = useRef(null);

  useEffect(() => {
    if (running) intRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(intRef.current);
  }, [running]);

  const stop = async () => {
    setRunning(false);
    const dur = +(seconds / 60).toFixed(2);
    if (dur < 0.5) return toast.error("Session too short");
    const r = await api.post("/passive", {
      activity_type: activity, duration_min: dur, posture_quality: quality,
    });
    toast.success(`+${r.data.passive_steps} passive steps · +${r.data.points_earned} pts`);
    setTimeout(() => nav("/home"), 600);
  };

  const min = String(Math.floor(seconds / 60)).padStart(2, "0");
  const sec = String(seconds % 60).padStart(2, "0");

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="passive-back">
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="ps-heading text-3xl font-bold mt-4">Passive Posture</h1>
        <p className="text-slate-400 text-sm">Earn credit for great posture while stationary. (50% of active value.)</p>

        <div className="mt-6 ps-card p-6 text-center">
          <Coffee size={32} className="mx-auto text-[#00B4D8]" />
          <div className="text-6xl font-bold ps-heading mt-3" data-testid="passive-timer">{min}:{sec}</div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Session timer</div>

          {running ? (
            <button data-testid="passive-stop" onClick={stop} className="mt-5 w-full ps-button-primary inline-flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#dc2626]">
              <Square size={18} /> Stop & Save
            </button>
          ) : (
            <button data-testid="passive-start" onClick={() => { setRunning(true); setSeconds(0); }} className="mt-5 w-full ps-button-primary inline-flex items-center justify-center gap-2">
              <Play size={18} /> Start Session
            </button>
          )}
        </div>

        <div className="mt-5 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Activity</div>
          <div className="grid grid-cols-3 gap-2">
            {ACTS.map((a) => (
              <button
                key={a.k}
                data-testid={`passive-act-${a.k}`}
                onClick={() => setActivity(a.k)}
                className={`px-3 py-2.5 rounded-xl text-xs font-medium border ${
                  activity === a.k ? "bg-[#142534] border-[#2fbf71] text-white" : "border-[#1a2f40] text-slate-400"
                }`}
              >
                {a.l}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 ps-card p-5">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Posture quality</div>
            <div className="text-[#2fbf71] font-semibold">{quality}/10</div>
          </div>
          <input
            data-testid="passive-quality"
            type="range" min="1" max="10" value={quality}
            onChange={(e) => setQuality(+e.target.value)}
            className="ps-slider mt-3"
          />
          <p className="text-xs text-slate-500 mt-2">Rate honestly. Higher quality = more passive steps awarded.</p>
        </div>
      </div>
    </PhoneFrame>
  );
}
